// Spec-compliant Server-Sent Events parser.
// https://html.spec.whatwg.org/multipage/server-sent-events.html#event-stream-interpretation

export interface SseEvent {
  event: string;
  data: string;
  id: string;
}

const empty = (): SseEvent => ({ event: "", data: "", id: "" });

const dispatch = (
  current: SseEvent,
  onEvent: (event: SseEvent) => void
): SseEvent => {
  if (current.data !== "") {
    // Strip the trailing newline added by `data:` line accumulation.
    const data = current.data.endsWith("\n")
      ? current.data.slice(0, -1)
      : current.data;
    onEvent({ ...current, data });
  }
  return empty();
};

const consumeLine = (line: string, current: SseEvent): SseEvent => {
  if (line === "" || line.startsWith(":")) return current;

  const colon = line.indexOf(":");
  const field = colon === -1 ? line : line.slice(0, colon);
  let value = colon === -1 ? "" : line.slice(colon + 1);
  if (value.startsWith(" ")) value = value.slice(1);

  if (field === "data") {
    return { ...current, data: current.data + value + "\n" };
  }
  if (field === "event") {
    return { ...current, event: value };
  }
  if (field === "id") {
    return { ...current, id: value };
  }
  return current;
};

export const readSseStream = async (
  response: Response,
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  if (!response.body) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let current = empty();

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel();
        return;
      }
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      buffer = buffer.replace(/\r\n?/g, "\n");

      let nlIndex = buffer.indexOf("\n");
      while (nlIndex !== -1) {
        const line = buffer.slice(0, nlIndex);
        buffer = buffer.slice(nlIndex + 1);

        if (line === "") {
          current = dispatch(current, onEvent);
        } else {
          current = consumeLine(line, current);
        }

        nlIndex = buffer.indexOf("\n");
      }
    }

    // Drain any final line that wasn't newline-terminated.
    if (buffer !== "") {
      current = consumeLine(buffer, current);
    }
    dispatch(current, onEvent);
  } finally {
    reader.releaseLock();
  }
};
